import React, { useEffect, useState } from 'react';
import Janitor from '../../../modules/Janitor';
import { PLATFORM_CHANNELS, COPY_LINK } from './constants';
import ShareMenu from './ShareMenu';
import social from './social';
import { getSharingLinks } from './social/helpers';
import { useMount } from '../../../hooks';
import { isAnonymous, ShareChannel } from '../../../modules/share';
import { ShareProps } from './types';

export type ShareMenuContainerProps = Omit<ShareProps, 'mode'> & {
  showCopyableLink?: boolean;
  performDirectShare?: () => void;
  renderShareModalContent?: (props) => React.ReactElement;
  onShowShareMenuSuccessModal?: () => void;
  closeShareModal?: () => void;
};

/**
 * The Share containing the logic for fetching the channel's target URLs and navigating to the
 * associated share locations.
 */
const ShareMenuContainer: React.FCWithChildren<ShareMenuContainerProps> = ({
  user,
  target,
  campaign = 'sharing_referral',
  message,
  provider,
  shorten = true,
  onShareAbort,
  onShareComplete,
  onShareSelect,
  renderShareModalContent,
  showCopyableLink,
  performDirectShare,
  onShowShareMenuSuccessModal,
  closeShareModal,
}) => {
  // Detect for unintentional missing, conditionally required, and unsupported props
  if (typeof user === 'undefined') {
    throw new Error('Argument `user` must be null or an instance of UserPropType');
  }
  if (target === 'profile' && !provider) {
    throw new Error('Argument `provider` is required when `target` is "profile"');
  }
  if (target === 'search' && !provider) {
    throw new Error('Argument `provider` is required when `target` is "search"');
  }

  const [urls, setUrls] = useState(null);
  const [pendingClickId, setPendingClickId] = useState(null);

  const channelsOrDefault = PLATFORM_CHANNELS;

  const handleReportError = error => {
    // eslint-disable-next-line no-console
    if (error) console.error(error);
    if (onShareAbort) onShareAbort(error);
  };

  const handleGetUrls = (ids => {
    const userId = !isAnonymous(user) ? user.user_id : 'guest';
    const providerId = provider?.id;
    const additionalParams = message ? message.urlParams : [];
    // also get a shortened link for copying to clipboard when in ATL-4145
    const linkIDs = showCopyableLink ? [
      ...ids,
      COPY_LINK,
    ] : ids;

    return getSharingLinks(target, providerId, shorten, linkIDs.map(id => ({
      id,
      parameters: [
        { key: 'utm_medium', value: id },
        { key: 'utm_campaign', value: `${campaign}_${userId}` },
        // Need to conditionally attach this, as a null value
        // will break the request to the sharing_links endpoint
        ...(providerId ? [{ key: 'utm_term', value: `provider_${providerId}` }] : []),
        ...additionalParams,
      ],
    })));
  });

  const handlePerformShare = (id => {
    const shareFunc = social[id][target](provider, urls[id], user, () => onShareComplete(id), handleReportError, message);

    if (!shareFunc) {
      handleReportError(new Error(`Unsupported channel "${id}"`));
      return;
    }

    if (onShareSelect) onShareSelect(id);

    shareFunc();
  });

  const handleClick = (id: ShareChannel) => {
    // If still loading, defer the click until the URLs resolve
    if (!urls) {
      setPendingClickId(prevPendingClickId => prevPendingClickId || id);
      return;
    }

    handlePerformShare(id);
  };

  useMount(() => {
    const janitor = new Janitor();

    // Ensure load function return a promise and that it runs within a promise to handle errors
    const promise = Promise.resolve().then(() => handleGetUrls(channelsOrDefault));

    janitor.addPromise(promise)
      .then(setUrls)
      .catch(handleReportError);
    return janitor.cleanup;
  });

  useEffect(() => {
    // Handle click if pending when URLs resolve
    // Note that both state variables are set at most once
    if (urls && pendingClickId) {
      handleClick(pendingClickId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urls,
    pendingClickId,
  ]);

  // Loading if URLs are not loaded or a click is pending (but not both)
  const showLoading = (pendingClickId !== null) && !urls;

  if (typeof renderShareModalContent === 'function') {
    return (renderShareModalContent({
      channels: channelsOrDefault,
      loading: showLoading,
      copyableShareLink: urls?.copy_link || '',
      performDirectShare,
      onShowShareMenuSuccessModal,
      onShareSelect,
      onShareComplete,
      closeShareModal,
    }));
  }

  return (
    <ShareMenu
      urls={urls}
      channels={channelsOrDefault}
      loading={showLoading}
      onClick={handleClick}
    />
  );
};

export default ShareMenuContainer;
